import React, { useContext, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import styles from "./LatestInsights.module.css";
import { LanguageContext } from "../LanguageContext/LanguageContext.jsx";
import { FaHeart } from "react-icons/fa";
import { useFilter } from "../FilterContext/FilterContext";

const marketingBackendUrl = import.meta.env.VITE_MARKETING_BACKEND_URL;

const LatestInsights = ({ searchTerm }) => {
  const { selectedCategories } = useFilter();
  const [allInsights, setAllInsights] = useState([]);
  const [filteredInsights, setFilteredInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleCount, setVisibleCount] = useState(2);
  const { direction } = useContext(LanguageContext);
  const navigate = useNavigate();
  const [wishlistStatus, setWishlistStatus] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const [notification, setNotification] = useState({
    show: false,
    message: "",
  });

  const filterAndSortInsights = useCallback(
    (insights) => {
      let filtered = [...insights];

      if (selectedCategories.length > 0) {
        filtered = insights.filter((insight) =>
          selectedCategories.includes(insight.category)
        );
      }

      // Filter by search term across all content
      if (searchTerm) {
        filtered = filtered.filter((insight) =>
          Object.values(insight).some(
            (value) =>
              typeof value === "string" &&
              value.toLowerCase().includes(searchTerm.toLowerCase())
          )
        );
      }

      return filtered.sort((a, b) => (a.rank || 0) - (b.rank || 0));
    },
    [selectedCategories, searchTerm]
  );

  useEffect(() => {
    const fetchInsights = async () => {
      try {
        const response = await axios.get(
          `${marketingBackendUrl}/maflam/insights/light`
        );
        if (response.data.success) {
          setAllInsights(response.data.data);
          setFilteredInsights(response.data.data);
        }
      } catch (err) {
        console.error("Error fetching insights:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchInsights();
  }, []);

  useEffect(() => {
    const filtered = filterAndSortInsights(allInsights);
    setFilteredInsights(filtered);
  }, [selectedCategories, allInsights, filterAndSortInsights]);

  useEffect(() => {
    const checkWishlistStatus = async () => {
      try {
        const userEmail = localStorage.getItem("useremailidis");
        if (!userEmail || !filteredInsights.length) return;

        const response = await axios.get(
          `${marketingBackendUrl}/maflam/blog-wishlist/${userEmail}`
        );

        if (response.data.success) {
          const wishlistBlogs = response.data.data;
          const statusMap = {};

          filteredInsights.forEach((blog) => {
            statusMap[blog._id] = wishlistBlogs.some(
              (wishlistBlog) => wishlistBlog.headingEnglish === blog.heading
            );
          });

          setWishlistStatus(statusMap);
        }
      } catch (error) {
        console.error("Error checking wishlist status:", error);
      }
    };

    if (filteredInsights.length > 0) {
      checkWishlistStatus();
    }
  }, [filteredInsights]);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const handleContinueReading = (id) => {
    navigate(`/home/insights/${id}`);
  };

  const handleWishlistToggle = async (blog) => {
    try {
      const userEmail = localStorage.getItem("useremailidis");

      if (!userEmail) {
        navigate("/home/login");
        return;
      }

      const isInWishlist = wishlistStatus[blog._id];

      if (isInWishlist) {
        // Remove from wishlist
        const response = await axios.post(
          `${marketingBackendUrl}/maflam/blog-wishlist/remove-blog`,
          {
            userEmail,
            headingEnglish: blog.heading,
          }
        );

        if (response.data.success) {
          setWishlistStatus((prev) => ({
            ...prev,
            [blog._id]: false,
          }));
          setNotification({
            show: true,
            message:
              direction === "rtl"
                ? "تمت إزالة المقال من قائمة الرغبات بنجاح"
                : "Insight removed from wishlist successfully",
          });
          setTimeout(() => {
            setNotification({ show: false, message: "" });
          }, 2000);
        }
      } else {
        // Add to wishlist
        const blogDetails = {
          headingEnglish: blog.heading,
          headingArabic: blog.heading,
          subheadingEnglish: blog.subheading,
          subheadingArabic: blog.subheading,
          thumbnail: blog.bannerImage,
          redirectionLink: `/home/insights/${blog._id}`,
        };

        const response = await axios.post(
          `${marketingBackendUrl}/maflam/blog-wishlist`,
          {
            userEmail,
            blogDetails,
          }
        );

        if (response.data.success) {
          setWishlistStatus((prev) => ({
            ...prev,
            [blog._id]: true,
          }));
          setNotification({
            show: true,
            message:
              direction === "rtl"
                ? "تمت إضافة المقال إلى قائمة الرغبات بنجاح"
                : "Insight added to wishlist successfully",
          });
          setTimeout(() => {
            setNotification({ show: false, message: "" });
          }, 2000);
        }
      }
    } catch (error) {
      console.error("Error updating wishlist:", error);
      if (error.response?.status === 400) {
        setNotification({
          show: true,
          message:
            direction === "rtl"
              ? "المقال موجود بالفعل في قائمة الرغبات"
              : "Insight already exists in wishlist",
        });
      } else {
        setNotification({
          show: true,
          message:
            direction === "rtl"
              ? "فشل في تحديث قائمة الرغبات"
              : "Failed to update wishlist",
        });
      }
    }
  };

  const handleLoadMore = () => {
    if (visibleCount >= filteredInsights.length) {
      setVisibleCount(2);
    } else {
      setVisibleCount((prev) => prev + 2);
    }
  };

  if (loading)
    return (
      <div className={styles.messageContainer}>
        <h2 className={styles.sectionHeading}>المقالات الأكثر شيوعًا </h2>
        <p className={styles.loadingMessage}>جاري التحميل...</p>
      </div>
    );

  if (error)
    return (
      <div className={styles.messageContainer}>
        <h2 className={styles.sectionHeading}>المقالات الأكثر شيوعًا </h2>
        <p className={styles.errorMessage}>حدث خطأ في تحميل البيانات</p>
      </div>
    );

  if (filteredInsights.length === 0)
    return (
      <div className={styles.messageContainer}>
        <h2 className={styles.sectionHeading}>المقالات الأكثر شيوعًا </h2>
        <p className={styles.noItemsMessage}>
          لا توجد مقالات حاليًا في هذه الفئة
        </p>
      </div>
    );

  return (
    <>
      {notification.show && (
        <div className={styles.notification}>{notification.message}</div>
      )}
      {isLoading && (
        <div className={styles.loaderWrapper}>
          <span className={styles.loader}></span>
        </div>
      )}
      <section style={{ direction: "rtl" }} className={styles.insightsSection}>
        <h2 className={styles.sectionHeading}> المقالات الأكثر شيوعًا </h2>
        <hr style={{ opacity: "0.2", marginBottom: "2rem" }} />
        <div className={styles.insightsContainer}>
          {filteredInsights.slice(0, visibleCount).map((insight, index) => (
            <div key={insight._id} className={styles.insightCard}>
              <button
                className={`${styles.wishlistButton} ${
                  wishlistStatus[insight._id] ? styles.inWishlist : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  handleWishlistToggle(insight);
                }}
              >
                <FaHeart
                  style={{
                    height: "20px",
                    width: "20px",
                    fill: wishlistStatus[insight._id]
                      ? "rgb(57, 255, 251)"
                      : "white",
                    pointerEvents: "none",
                  }}
                />
              </button>
              <img
                src={insight.bannerImage}
                alt={insight.heading}
                className={styles.insightImage}
              />
              <div className={styles.cardContent}>
                <h4>{insight.author}</h4>
                <p className={styles.title}>{insight.heading}</p>
                <a
                  onClick={() => handleContinueReading(insight._id)}
                  className={styles.readMore}
                >
                  اقرأ المزيد
                  <span
                    style={{
                      color: "white",
                      fontWeight: "700",
                      display: "inline-block",
                    }}
                  >
                    <svg
                      xmlns="https://www.w3.org/2000/svg"
                      width="15"
                      height="12"
                      viewBox="0 0 15 12"
                      fill="none"
                    >
                      <path
                        d="M13.9995 6L1.14237 6M1.14237 6L6.14237 11M1.14237 6L6.14237 0.999999"
                        stroke="#F7FFFF"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </a>
              </div>
            </div>
          ))}
        </div>

        {filteredInsights.length > 2 && (
          <button
            onClick={handleLoadMore}
            className={styles.loadMoreBtn}
            style={{
              backgroundColor: "#39FFFB",
              // color: "black",
              // height: "64px",
              // borderRadius: "32px",
              // fontSize: "17px",
              // fontWeight: "700",
              // backgroundColor: "#061c2b",
              margin: "auto",
              padding: "24px 32px",
              gap: "8px",
              borderRadius: "64px",
              fontSize: "17px",
              color:"black",
              fontWeight: "700",

            }}
          >
            {visibleCount >= filteredInsights.length
              ? "عرض أقل"
              : "تحميل المزيد"}
          </button>
        )}
      </section>
    </>
  );
};

export default LatestInsights;
